import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { Collapse, alpha } from "@mui/material";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { RoleGuard } from "../../../components/RBAC";
import { menuCategories } from "./menu";
import { Logo } from "../../../assets";

// Premium sidebar design tokens
const sidebar = {
  width: 272,
  bg: "linear-gradient(180deg, #0F172A 0%, #1E293B 100%)",
  textPrimary: "#E2E8F0",
  textSecondary: "#94A3B8",
  textMuted: "#64748B",
  activeColor: "#D58E4E",
  activeBg: "rgba(213, 142, 78, 0.12)",
  activeGlow: "rgba(213, 142, 78, 0.06)",
  hoverBg: "rgba(255, 255, 255, 0.04)",
  divider: "rgba(255, 255, 255, 0.06)",
  categoryText: "#64748B",
};

function SideNav() {
  const location = useLocation();
  const [openCategories, setOpenCategories] = useState<{
    [key: string]: boolean;
  }>(() => {
    // Auto-open the category that contains the current route
    const initial: { [key: string]: boolean } = {};
    menuCategories.forEach((cat) => {
      const hasActive = cat.items.some((item) =>
        location.pathname.startsWith(item.path)
      );
      if (hasActive) initial[cat.category] = true;
    });
    if (Object.keys(initial).length === 0) initial["Dashboard"] = true;
    return initial;
  });

  const handleCategoryClick = (category: string) => {
    setOpenCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  return (
    <Box
      sx={{
        width: `${sidebar.width}px`,
        height: "100vh",
        overflow: "hidden",
        position: "fixed",
        background: sidebar.bg,
        color: sidebar.textPrimary,
        display: "flex",
        flexDirection: "column",
        zIndex: 1200,
        borderRight: "1px solid rgba(255, 255, 255, 0.04)",
      }}
    >
      {/* Logo Section */}
      <Box
        sx={{
          px: 2.5,
          py: 2.5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          minHeight: 72,
          borderBottom: `1px solid ${sidebar.divider}`,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            background: "linear-gradient(135deg, #D58E4E 0%, #E9A767 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 12px rgba(213, 142, 78, 0.3)",
          }}
        >
          <img
            src={Logo}
            alt="Talaqah"
            style={{
              width: 24,
              height: 24,
              filter: "brightness(10)",
            }}
          />
        </Box>
        <Box>
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 700,
              color: "#FFFFFF",
              letterSpacing: "-0.02em",
              lineHeight: 1.2,
            }}
          >
            Talaqah
          </Typography>
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 500,
              color: sidebar.textMuted,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            Admin Panel
          </Typography>
        </Box>
      </Box>

      {/* Navigation */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          py: 1.5,
          px: 1.5,
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(255, 255, 255, 0.08)",
            borderRadius: "99px",
          },
        }}
      >
        <List sx={{ px: 0 }}>
          {menuCategories.map((category, categoryIndex) => (
            <Box key={categoryIndex} sx={{ mb: 0.25 }}>
              {/* Category Divider - subtle label */}
              {categoryIndex > 0 && (
                <Divider
                  sx={{
                    borderColor: sidebar.divider,
                    my: 1,
                    mx: 1,
                  }}
                />
              )}

              <ListItemButton
                onClick={() => handleCategoryClick(category.category)}
                sx={{
                  borderRadius: "10px",
                  mb: 0.25,
                  py: 1,
                  px: 1.5,
                  transition: "all 0.2s cubic-bezier(0.16, 1, 0.3, 1)",
                  "&:hover": {
                    background: sidebar.hoverBg,
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 34,
                    color: openCategories[category.category]
                      ? sidebar.activeColor
                      : sidebar.textMuted,
                    transition: "color 0.2s ease",
                    "& svg": { fontSize: 20 },
                  }}
                >
                  {category.icon}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: openCategories[category.category]
                          ? sidebar.textPrimary
                          : sidebar.textSecondary,
                        letterSpacing: "0.01em",
                        transition: "color 0.2s ease",
                      }}
                    >
                      {category.category}
                    </Typography>
                  }
                />
                {openCategories[category.category] ? (
                  <ExpandLess
                    sx={{
                      fontSize: 16,
                      color: sidebar.textMuted,
                      transition: "transform 0.2s ease",
                    }}
                  />
                ) : (
                  <ExpandMore
                    sx={{
                      fontSize: 16,
                      color: sidebar.textMuted,
                      transition: "transform 0.2s ease",
                    }}
                  />
                )}
              </ListItemButton>

              <Collapse
                in={openCategories[category.category]}
                timeout={200}
                unmountOnExit
              >
                <List component="div" disablePadding>
                  {category.items.map((item, itemIndex) => {
                    const isActive = location.pathname.startsWith(item.path);

                    const menuItem = (
                      <Link
                        to={item.path}
                        key={itemIndex}
                        style={{
                          textDecoration: "none",
                          color: "inherit",
                        }}
                      >
                        <ListItemButton
                          selected={isActive}
                          sx={{
                            borderRadius: "10px",
                            py: 0.75,
                            px: 1.5,
                            ml: 1,
                            mb: 0.25,
                            position: "relative",
                            transition:
                              "all 0.2s cubic-bezier(0.16, 1, 0.3, 1)",
                            "&.Mui-selected": {
                              background: sidebar.activeBg,
                              boxShadow: `inset 0 0 0 1px rgba(213, 142, 78, 0.15)`,
                              "& .menu-text": {
                                color: sidebar.activeColor,
                                fontWeight: 600,
                              },
                              "& .menu-icon": {
                                color: sidebar.activeColor,
                                opacity: 1,
                              },
                              "&:hover": {
                                background: sidebar.activeBg,
                              },
                            },
                            "&:hover": {
                              background: sidebar.hoverBg,
                            },
                          }}
                        >
                          {/* Active indicator bar */}
                          {isActive && (
                            <Box
                              sx={{
                                position: "absolute",
                                left: 0,
                                top: "50%",
                                transform: "translateY(-50%)",
                                width: 3,
                                height: 20,
                                borderRadius: "0 4px 4px 0",
                                backgroundColor: sidebar.activeColor,
                                boxShadow: `0 0 8px ${sidebar.activeColor}`,
                              }}
                            />
                          )}
                          <ListItemIcon
                            className="menu-icon"
                            sx={{
                              minWidth: 30,
                              color: isActive
                                ? sidebar.activeColor
                                : sidebar.textMuted,
                              opacity: isActive ? 1 : 0.7,
                              transition: "all 0.2s ease",
                              "& svg": { fontSize: 18 },
                            }}
                          >
                            {item.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography
                                className="menu-text"
                                sx={{
                                  fontSize: "13px",
                                  fontWeight: isActive ? 600 : 400,
                                  color: isActive
                                    ? sidebar.activeColor
                                    : sidebar.textSecondary,
                                  transition: "all 0.2s ease",
                                  letterSpacing: "0.005em",
                                }}
                              >
                                {item.title}
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      </Link>
                    );

                    if (item.requiresSuperAdmin) {
                      return (
                        <RoleGuard
                          key={itemIndex}
                          allowedRoles={["SUPER_ADMIN"]}
                        >
                          {menuItem}
                        </RoleGuard>
                      );
                    }

                    return menuItem;
                  })}
                </List>
              </Collapse>
            </Box>
          ))}
        </List>
      </Box>

      {/* Bottom Section */}
      <Box
        sx={{
          px: 2,
          py: 2,
          borderTop: `1px solid ${sidebar.divider}`,
        }}
      >
        <Box
          sx={{
            background: "rgba(255, 255, 255, 0.04)",
            borderRadius: "10px",
            p: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: "#22C55E",
              boxShadow: "0 0 8px rgba(34, 197, 94, 0.5)",
            }}
          />
          <Typography
            sx={{
              fontSize: "11px",
              color: sidebar.textMuted,
              fontWeight: 500,
            }}
          >
            System Online
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
export default SideNav;
